import React, { useCallback, useMemo, useRef, useState } from 'react';
import { formatPower, formatKwh } from './geradorasShared';

/**
 * Curva de potência da usina, em SVG, com cruzeta e tooltip.
 *
 * Substitui as barras de <div> que existiam antes, que tinham dois problemas:
 * o tooltip era o atributo `title` nativo (lento, e impossível de acertar numa
 * barra de 6px — o usuário reportou que passar o mouse "não mostra nada"), e a
 * altura tinha piso de 6%, então usina parada virava uma fileira de tocos
 * iguais em vez de mostrar que não gerou nada.
 *
 * Duas séries no MESMO eixo (ambas em W): pico como faixa e média como linha.
 * Energia e nº de amostras aparecem só no tooltip — não ganham segundo eixo.
 */

export type PontoSerie = {
    bucket: string;
    potencia_media_w: number;
    potencia_pico_w: number;
    geracao_kwh: number;
    amostras: number;
};

export type Granularidade = 'hora' | 'dia' | 'mes';

// Slots 1 e 2 da paleta de referência, validados para daltonismo e contraste
// contra as duas superfícies (claro #fcfcfb, escuro #1a1a19).
const AZUL = { claro: '#2a78d6', escuro: '#3987e5' };
const LARANJA = { claro: '#eb6834', escuro: '#d95926' };

const VB_W = 800;
const VB_H = 250;
// left precisa caber o maior rótulo do eixo Y. Com 56 o "552,35 kW" do Bloco 1
// nascia em x=-7 e o SVG cortava o primeiro dígito, virando "52,35 kW" — uma
// ordem de grandeza a menos, e nada na tela denunciando.
const PAD = { top: 14, right: 14, bottom: 26, left: 76 };

const escalaY = (v: number, max: number) => {
    const util = VB_H - PAD.top - PAD.bottom;
    return PAD.top + util - (max > 0 ? (v / max) * util : 0);
};

function rotuloX(iso: string, g: Granularidade): string {
    const d = new Date(iso);
    if (g === 'mes') return d.toLocaleDateString('pt-BR', { month: 'short', year: '2-digit' });
    if (g === 'dia') return d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
    return d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
}

function rotuloCompleto(iso: string, g: Granularidade): string {
    const d = new Date(iso);
    if (g === 'mes') return d.toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' });
    if (g === 'dia') return d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' });
    return d.toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
}

const CurvaPotencia: React.FC<{
    pontos: PontoSerie[];
    granularidade: Granularidade;
    carregando?: boolean;
}> = ({ pontos, granularidade, carregando = false }) => {
    const svgRef = useRef<SVGSVGElement>(null);
    const [idx, setIdx] = useState<number | null>(null);

    const maxW = useMemo(() => Math.max(...pontos.map(p => p.potencia_pico_w), 1), [pontos]);
    const tudoZero = useMemo(
        () => pontos.length > 0 && pontos.every(p => !p.potencia_pico_w), [pontos]);

    const x = useCallback((i: number) => {
        const util = VB_W - PAD.left - PAD.right;
        return PAD.left + (pontos.length <= 1 ? util / 2 : (i / (pontos.length - 1)) * util);
    }, [pontos.length]);

    // A cruzeta acha o X mais próximo: o leitor mira num horário, não num traço
    // de 2px. Sem isso o hover só responderia em cima da linha.
    const aoMover = useCallback((e: React.PointerEvent<SVGSVGElement>) => {
        const svg = svgRef.current;
        if (!svg || pontos.length === 0) return;
        const r = svg.getBoundingClientRect();
        const px = ((e.clientX - r.left) / r.width) * VB_W;
        const util = VB_W - PAD.left - PAD.right;
        const bruto = ((px - PAD.left) / util) * (pontos.length - 1);
        setIdx(Math.max(0, Math.min(pontos.length - 1, Math.round(bruto))));
    }, [pontos.length]);

    // Mesmo detalhe no teclado que no mouse — o tooltip não pode ser o único
    // caminho para o número.
    const aoTeclado = useCallback((e: React.KeyboardEvent) => {
        if (pontos.length === 0) return;
        if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') {
            e.preventDefault();
            setIdx(prev => Math.max(0, Math.min(
                pontos.length - 1, (prev ?? 0) + (e.key === 'ArrowRight' ? 1 : -1))));
        } else if (e.key === 'Escape') setIdx(null);
    }, [pontos.length]);

    if (!carregando && pontos.length === 0) {
        return (
            <div className="h-64 rounded-xl border border-dashed border-gray-300 dark:border-gray-600 flex items-center justify-center text-sm text-gray-500 dark:text-gray-400 text-center px-6">
                Sem telemetria contínua nesta janela.
            </div>
        );
    }

    const areaPico = pontos.length
        ? `M ${x(0)} ${escalaY(0, maxW)} `
          + pontos.map((p, i) => `L ${x(i)} ${escalaY(p.potencia_pico_w, maxW)}`).join(' ')
          + ` L ${x(pontos.length - 1)} ${escalaY(0, maxW)} Z`
        : '';
    const linhaMedia = pontos
        .map((p, i) => `${i === 0 ? 'M' : 'L'} ${x(i)} ${escalaY(p.potencia_media_w, maxW)}`)
        .join(' ');

    // Com tudo zerado o maxW cai no piso de 1 W e os três ticks viram
    // "0 W / 1 W / 1 W" — dois rótulos idênticos. Aí uma linha de base basta.
    const ticksY = tudoZero ? [0] : [0, 0.5, 1].map(f => f * maxW);
    // No máximo 6 rótulos no eixo X, senão colidem em janelas longas.
    const passoX = Math.max(1, Math.ceil(pontos.length / 6));
    const sel = idx != null ? pontos[idx] : null;
    const tooltipADireita = sel != null && x(idx!) / VB_W > 0.58;

    return (
        <div className="relative">
            {/* Recarga mantém o quadro: o gráfico anterior fica esmaecido em vez
                de virar esqueleto, então a caixa não pula de tamanho. */}
            <div className={carregando ? 'opacity-40 transition-opacity' : 'transition-opacity'}>
                <svg
                    ref={svgRef}
                    viewBox={`0 0 ${VB_W} ${VB_H}`}
                    className="w-full h-64 touch-none rounded-xl bg-gray-50/70 dark:bg-gray-900/40 outline-none focus-visible:ring-2 focus-visible:ring-primary"
                    onPointerMove={aoMover}
                    onPointerLeave={() => setIdx(null)}
                    onKeyDown={aoTeclado}
                    tabIndex={0}
                    role="img"
                    aria-label={`Curva de potência, ${pontos.length} pontos. Use as setas para percorrer.`}
                >
                    {ticksY.map(v => (
                        <g key={v}>
                            <line
                                x1={PAD.left} x2={VB_W - PAD.right}
                                y1={escalaY(v, maxW)} y2={escalaY(v, maxW)}
                                className="stroke-gray-200 dark:stroke-gray-700" strokeWidth={1}
                            />
                            <text
                                x={PAD.left - 8} y={escalaY(v, maxW) + 4} textAnchor="end"
                                className="fill-gray-400 dark:fill-gray-500" fontSize={11}
                            >{formatPower(v)}</text>
                        </g>
                    ))}

                    {pontos.map((p, i) => (i % passoX === 0 ? (
                        <text
                            key={p.bucket} x={x(i)} y={VB_H - 8} textAnchor="middle"
                            className="fill-gray-400 dark:fill-gray-500" fontSize={11}
                        >{rotuloX(p.bucket, granularidade)}</text>
                    ) : null))}

                    {tudoZero ? (
                        <line
                            x1={PAD.left} x2={VB_W - PAD.right}
                            y1={escalaY(0, maxW)} y2={escalaY(0, maxW)}
                            className="stroke-gray-300 dark:stroke-gray-600"
                            strokeWidth={2} strokeDasharray="4 4"
                        />
                    ) : (
                        <>
                            <path d={areaPico} fill={LARANJA.claro} opacity={0.18} className="dark:hidden" />
                            <path d={areaPico} fill={LARANJA.escuro} opacity={0.24} className="hidden dark:block" />
                            <path d={linhaMedia} fill="none" strokeWidth={2}
                                strokeLinejoin="round" strokeLinecap="round"
                                stroke={AZUL.claro} className="dark:hidden" />
                            <path d={linhaMedia} fill="none" strokeWidth={2}
                                strokeLinejoin="round" strokeLinecap="round"
                                stroke={AZUL.escuro} className="hidden dark:block" />
                        </>
                    )}

                    {sel ? (
                        <g pointerEvents="none">
                            <line
                                x1={x(idx!)} x2={x(idx!)} y1={PAD.top} y2={VB_H - PAD.bottom}
                                className="stroke-gray-400 dark:stroke-gray-500"
                                strokeWidth={1} strokeDasharray="3 3"
                            />
                            {!tudoZero ? (
                                <>
                                    {/* anel na cor da superfície separa o marcador da faixa */}
                                    <circle cx={x(idx!)} cy={escalaY(sel.potencia_pico_w, maxW)} r={4.5}
                                        fill={LARANJA.claro} stroke="#fcfcfb" strokeWidth={2} className="dark:hidden" />
                                    <circle cx={x(idx!)} cy={escalaY(sel.potencia_pico_w, maxW)} r={4.5}
                                        fill={LARANJA.escuro} stroke="#1a1a19" strokeWidth={2} className="hidden dark:block" />
                                    <circle cx={x(idx!)} cy={escalaY(sel.potencia_media_w, maxW)} r={4.5}
                                        fill={AZUL.claro} stroke="#fcfcfb" strokeWidth={2} className="dark:hidden" />
                                    <circle cx={x(idx!)} cy={escalaY(sel.potencia_media_w, maxW)} r={4.5}
                                        fill={AZUL.escuro} stroke="#1a1a19" strokeWidth={2} className="hidden dark:block" />
                                </>
                            ) : null}
                        </g>
                    ) : null}
                </svg>
            </div>

            {tudoZero ? (
                <div className="absolute inset-x-0 top-1/2 -translate-y-1/2 text-center pointer-events-none">
                    <p className="text-sm font-semibold text-gray-500 dark:text-gray-400">Sem geração no período</p>
                    <p className="text-xs text-gray-400 dark:text-gray-500">
                        As {pontos.length} amostras marcam 0 W.
                    </p>
                </div>
            ) : null}

            {/* Duas séries, então a legenda é obrigatória. Traço e faixa espelham
                as marcas que nomeiam. */}
            <div className="flex items-center gap-4 mt-2 text-xs text-gray-500 dark:text-gray-400">
                <span className="inline-flex items-center gap-1.5">
                    <svg width="16" height="4" aria-hidden="true"><rect width="16" height="4" rx="2" fill={AZUL.claro} /></svg>
                    Média
                </span>
                <span className="inline-flex items-center gap-1.5">
                    <svg width="16" height="8" aria-hidden="true"><rect width="16" height="8" rx="2" fill={LARANJA.claro} opacity={0.35} /></svg>
                    Pico
                </span>
                <span className="ml-auto hidden sm:inline text-gray-400 dark:text-gray-500">
                    Passe o mouse ou use as setas
                </span>
            </div>

            {sel ? (
                <div
                    className="absolute z-10 pointer-events-none rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 shadow-lg px-3 py-2 text-xs w-[176px]"
                    style={{
                        left: `${(x(idx!) / VB_W) * 100}%`,
                        marginLeft: tooltipADireita ? -188 : 12,
                        top: 8,
                    }}
                >
                    <div className="font-semibold text-gray-700 dark:text-gray-200 mb-1.5">
                        {rotuloCompleto(sel.bucket, granularidade)}
                    </div>
                    {/* Valor primeiro, rótulo depois: aqui o leitor já sabe a série
                        e quer o número. */}
                    <div className="space-y-1">
                        <div className="flex items-center gap-2">
                            <svg width="12" height="3" aria-hidden="true"><rect width="12" height="3" rx="1.5" fill={AZUL.claro} /></svg>
                            <span className="font-bold tabular-nums text-gray-900 dark:text-white">{formatPower(sel.potencia_media_w)}</span>
                            <span className="text-gray-500 dark:text-gray-400">média</span>
                        </div>
                        <div className="flex items-center gap-2">
                            <svg width="12" height="3" aria-hidden="true"><rect width="12" height="3" rx="1.5" fill={LARANJA.claro} /></svg>
                            <span className="font-bold tabular-nums text-gray-900 dark:text-white">{formatPower(sel.potencia_pico_w)}</span>
                            <span className="text-gray-500 dark:text-gray-400">pico</span>
                        </div>
                        <div className="pt-1 mt-1 border-t border-gray-100 dark:border-gray-700 flex justify-between text-gray-500 dark:text-gray-400">
                            <span>Energia</span>
                            <span className="font-semibold tabular-nums text-gray-700 dark:text-gray-200">{formatKwh(sel.geracao_kwh)}</span>
                        </div>
                        <div className="flex justify-between text-gray-400 dark:text-gray-500">
                            <span>Amostras</span>
                            <span className="tabular-nums">{sel.amostras}</span>
                        </div>
                    </div>
                </div>
            ) : null}
        </div>
    );
};

export default CurvaPotencia;
